<template>
  <div>
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px">
      <div class="caption grey--text text-left" style="max-width: 520px">
        Suba la distribución por local (Excel) para generar las guías de despacho de esta OT. Puede haber varias
        distribuciones (una por envío); cada una se revisa y edita antes de emitir.
      </div>
      <v-spacer />
      <v-btn small text color="primary" :loading="downloading" @click="downloadTemplate">
        <v-icon left small>fas fa-download</v-icon>Descargar plantilla
      </v-btn>
      <v-btn small color="primary" class="font-weight-bold" @click="importDialog = true">
        <v-icon left small>fas fa-plus</v-icon>Nueva distribución
      </v-btn>
    </div>

    <v-data-table
      :headers="headers" :items="batches" :loading="loading" dense hide-default-footer :items-per-page="-1"
      no-data-text="Esta OT aún no tiene distribuciones" class="border mb-4 row-pointer" @click:row="open"
    >
      <template v-slot:item.name="{ item }">
        <span class="font-weight-bold">#{{ item.id }}</span>
        <span class="ml-1">{{ item.name || item.file_name }}</span>
      </template>
      <template v-slot:item.created_at="{ item }">{{ item.created_at | moment('DD/MM/YYYY') }}</template>
      <template v-slot:item.locales="{ item }">{{ item.summary.total }}</template>
      <template v-slot:item.total_units="{ item }">{{ fmt(item.total_units) }}</template>
      <template v-slot:item.distribution_status="{ item }">
        <v-chip x-small :color="statusMeta(item).color" dark>{{ statusMeta(item).text }}</v-chip>
        <span v-if="item.summary.emitted && item.distribution_status !== 'emitida'" class="caption grey--text ml-1">
          {{ item.summary.emitted }}/{{ item.summary.total }}
        </span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon x-small title="Ver / editar" @click.stop="open(item)"><v-icon x-small>fas fa-eye</v-icon></v-btn>
        <v-btn icon x-small title="Descargar Excel" :loading="downloadingId === item.id" @click.stop="downloadBatch(item)"><v-icon x-small>fas fa-file-arrow-down</v-icon></v-btn>
        <v-btn v-if="item.can_delete" icon x-small title="Eliminar" :loading="deletingId === item.id" @click.stop="askDelete(item)"><v-icon x-small color="error">fas fa-trash</v-icon></v-btn>
      </template>
    </v-data-table>

    <div class="caption grey--text font-weight-black text-uppercase mb-2 text-left">Resumen por producto</div>
    <product-summary-table :products="summary" show-price />
    <div class="caption grey--text mt-1 text-left">Las diferencias entre lo distribuido y lo cotizado solo se advierten. El precio neto de cada producto se toma de la cotización al emitir.</div>

    <bulk-dispatch-import-dialog v-model="importDialog" :work-order-id="workOrderId" @loaded="onLoaded" />
    <confirm v-model="confirmShow" :message="confirmMessage" @ok="removeBatch" @cancel="confirmShow = false" />
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import BulkDispatchImportDialog from '@/components/facturacioncl/BulkDispatchImportDialog.vue'
import ProductSummaryTable from '@/components/facturacioncl/DistributionProductSummary.vue'
import { DISTRIBUTION_STATUS } from '@/utils/distributions'
import {
  listDispatchBatches, getDispatchSummary, deleteDispatchBatch, downloadDispatchTemplate,
} from '@/api/facturacioncl'

export default {
  name: 'DistributionPanel',
  components: { BulkDispatchImportDialog, ProductSummaryTable },
  props: {
    workOrderId: { type: [Number, String], required: true },
  },
  data() {
    return {
      batches: [],
      summary: [],
      loading: false,
      downloading: false,
      downloadingId: null,
      deletingId: null,
      importDialog: false,
      confirmShow: false,
      confirmMessage: '',
      toDelete: null,
      headers: [
        { text: 'Distribución', value: 'name' },
        { text: 'Fecha', value: 'created_at', width: 100 },
        { text: 'Locales', value: 'locales', align: 'end', width: 80 },
        { text: 'Unidades', value: 'total_units', align: 'end', width: 90 },
        { text: 'Estado', value: 'distribution_status', width: 140 },
        { text: '', value: 'actions', sortable: false, align: 'end', width: 110 },
      ],
    }
  },
  watch: {
    workOrderId: { immediate: true, handler() { this.fetch() } },
  },
  methods: {
    statusMeta(item) {
      return DISTRIBUTION_STATUS[item.distribution_status] || { text: item.distribution_status, color: 'grey' }
    },
    fmt(n) {
      return Number(n || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 })
    },
    fail(e, fallback) {
      notifications.showError(typeof e === 'string' ? e : fallback)
    },
    async fetch() {
      if (!this.workOrderId) return
      this.loading = true
      try {
        const [batches, summary] = await Promise.all([
          listDispatchBatches({ workorder: this.workOrderId }),
          getDispatchSummary(this.workOrderId),
        ])
        this.batches = batches
        this.summary = summary.products
      } catch (e) {
        this.fail(e, 'No se pudieron cargar las distribuciones')
      } finally {
        this.loading = false
      }
    },
    open(item) {
      this.$router.push({ name: 'BulkDispatchBatch', params: { batchId: item.id } })
    },
    onLoaded(batch) {
      this.open(batch)
    },
    async downloadTemplate() {
      this.downloading = true
      try {
        await downloadDispatchTemplate({ workorderId: this.workOrderId })
      } catch (e) {
        this.fail(e, 'No se pudo descargar la plantilla')
      } finally {
        this.downloading = false
      }
    },
    async downloadBatch(item) {
      this.downloadingId = item.id
      try {
        await downloadDispatchTemplate({ workorderId: this.workOrderId, batchId: item.id })
      } catch (e) {
        this.fail(e, 'No se pudo descargar la distribución')
      } finally {
        this.downloadingId = null
      }
    },
    askDelete(item) {
      this.toDelete = item
      this.confirmMessage = '¿Eliminar la distribución #' + item.id + (item.name ? ' (' + item.name + ')' : '') + ' y todas sus pre-guías?'
      this.confirmShow = true
    },
    async removeBatch() {
      this.confirmShow = false
      const item = this.toDelete
      if (!item) return
      this.deletingId = item.id
      try {
        await deleteDispatchBatch(item.id)
        notifications.showSuccess('Distribución eliminada')
        await this.fetch()
      } catch (e) {
        this.fail(e, 'No se pudo eliminar la distribución')
      } finally {
        this.deletingId = null
        this.toDelete = null
      }
    },
  },
}
</script>

<style scoped>
.row-pointer >>> tbody tr { cursor: pointer; }
</style>
